// @flow
import React, { Component, Fragment } from 'react';
import axios, { type Axios } from 'axios';
import ProductContent from 'components/product/ProductContent';
import ProductTags from 'components/product/ProductTags';
import { BaseActions, CommonActions, DisplayActions, ProductsActions } from 'store/actionCreators';
import { connect } from 'react-redux';
import type { State } from 'store';
import type { ProductData } from 'store/modules/products';
import QuestionModal from 'components/common/QuestionModal/QuestionModal';
import { type ContextRouter, type Location, withRouter } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { convertToPlainText } from 'lib/common';
import { track } from 'lib/tracking';
import { TRACKING_EVENTS } from 'lib/trackingEvents';
import ProductPlaceholder from 'components/product/ProductPlaceholder';
import throttle from 'lodash/throttle';
import ProductSeriesInfo from 'components/product/ProductSeriesInfo/ProductSeriesInfo';
import ProductRegulationExcReturn from 'components/product/ProductGuide';
import ProductCategories from 'components/product/ProductCategories/ProductCategories';

type Props = {
  username: ?string,
  urlSlug: ?string,
  product: ProductData,
  likeInProcess: boolean,
  currentUsername: ?string,
  askRemove: boolean,
  routerHistory: Location[],
  shouldCancel: boolean,
  logged: boolean,
} & ContextRouter;

class ProductViewer extends Component<Props> {
  onActivateHeading = throttle((headingId: string) => {
    ProductsActions.activateHeading(headingId);
  }, 250);

  initialize = async () => {
    const { username, urlSlug, shouldCancel, currentUserRule, currentUser } = this.props;
    const type = 'NORMAL';
    if (!username || !urlSlug || !type) return;
    try {
      if (shouldCancel) return;
      await ProductsActions.readProduct({
        username,
        urlSlug,
        type,
      });
      if (!this.props.product) return;
      const { id } = this.props.product;
      ProductsActions.getSequences(id);
      track(TRACKING_EVENTS.PRODUCT_VIEWED, {
        productId: this.props.product.id,
        productSlug: this.props.product.url_slug,
        productTitle: this.props.product.title,
        brand: this.props.product.meta && this.props.product.meta.supplier,
        supplierUsername: this.props.product.user && this.props.product.user.username,
      });
    } catch (e) {
      console.log(e);
      if (e && e.response && e.response.status === 404) {
        this.props.history.replace('/404');
      }
    }
  };

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.urlSlug !== this.props.urlSlug) {
      ProductsActions.unloadProduct();
      this.initialize();
    }
  }

  componentWillUnmount() {
    ProductsActions.unloadProduct();
  }

  // onSetToc = (toc: ?(TocItem[])) => {
  //   ProductsActions.setToc(toc);
  // };

  onToggleLike = () => {
    const { product, likeInProcess } = this.props;
    if (likeInProcess) return;
    if (!product) return;
    if (product.liked) {
      ProductsActions.unlike(product.id);
    } else {
      ProductsActions.like(product.id);
    }
  };

  onToggleAskRemove = () => {
    ProductsActions.toggleAskRemove();
  };

  onConfirmRemove = async () => {
    const { product, history, routerHistory } = this.props;
    ProductsActions.toggleAskRemove();
    if (!product) return;
    try {
      await CommonActions.removeProduct(product.id);
      DisplayActions.flushList();
    } catch (e) {
      console.log(e);
    }
    if (routerHistory.length === 0) {
      history.push('/');
      return;
    }
    history.goBack();
  };

  componentDidMount() {
    setTimeout(() => {
      this.initialize();
    }, 0);
    const { hash } = this.props.location;
    if (hash !== '') {
      ProductsActions.activateHeading(decodeURI(hash.split('#')[1]));
    }
  }

  informCopy = () => {
    BaseActions.showToast({
      message: '링크가 복사되었습니다',
      type: 'success',
    });
  };

  onLogin = () => {
    const { product, currentUser } = this.props;
    if (!currentUser) {
      this.props.history.push(
        `/login?next=/@${product.user.username}/products/${product.url_slug}`,
      );
    }
    return !!currentUser;
  };

  // 상품 정보 새로고침 함수
  refreshProductData = async () => {
    const { username, urlSlug } = this.props;
    const type = 'REFRESH';
    if (!username || !urlSlug || !type) return false;

    try {
      // 로딩 상태 표시
      BaseActions.showToast({
        message: '상품 정보를 새로고침 중입니다...',
        type: 'info',
      });

      // 상품 정보 다시 불러오기
      await ProductsActions.readProduct({
        username,
        urlSlug,
        type,
      });

      // 성공 메시지 표시
      BaseActions.showToast({
        message: '상품 정보가 업데이트되었습니다.',
        type: 'success',
      });

      return true;
    } catch (e) {
      BaseActions.showToast({
        message: '상품 정보 새로고침에 실패했습니다.',
        type: 'error',
      });
      return false;
    }
  };

  onChannel = async (rowData, option) => {
    if (!window.Tawk_API) return;
    window.Tawk_API.toggle();
    const { product, currentUser } = this.props;
    track(TRACKING_EVENTS.CHAT_WIDGET_OPENED, {
      productId: product && product.id,
      productSlug: product && product.url_slug,
      productTitle: product && product.title,
      partNumber: rowData && rowData.part_number,
    });
    const url = 'https://hooks.slack.com/services/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';
    const data = {
      channel: '#제품상담',
      username: 'cacheby-bot',
      text: `*🔔 새로운 제품 문의가 접수되었습니다 (${option})*`,
      blocks: [
        {
          type: 'header',
          text: {
            type: 'plain_text',
            text: `🔔 새로운 제품 문의가 접수되었습니다 (${option})`,
            emoji: true,
          },
        },
        {
          type: 'divider',
        },
        {
          type: 'section',
          fields: [
            {
              type: 'mrkdwn',
              text: `*문의자 정보:*\n👤 *이름:* ${
                currentUser ? currentUser.displayName : '비회원'
              }\n📧 *이메일:* ${currentUser ? currentUser.email : '비회원'}`,
            },
            {
              type: 'mrkdwn',
              text: `*문의 시간:*\n🕒 ${new Date().toLocaleString('ko-KR', {
                timeZone: 'Asia/Seoul',
              })}`,
            },
          ],
        },
        {
          type: 'divider',
        },
        {
          type: 'section',
          text: {
            type: 'mrkdwn',
            text: `*제품 정보:*`,
          },
        },
        {
          type: 'section',
          fields: [
            {
              type: 'mrkdwn',
              text: `*제품명:*\n${rowData ? rowData.description : '정보 없음'}`,
            },
            {
              type: 'mrkdwn',
              text: `*제품 번호:*\n${rowData ? rowData.part_number : '정보 없음'}`,
            },
          ],
        },
        {
          type: 'section',
          fields: [
            {
              type: 'mrkdwn',
              text: `*상태:*\n${rowData ? rowData.status : '정보 없음'}`,
            },
            {
              type: 'mrkdwn',
              text: `*단위:*\n${rowData ? rowData.unit : '정보 없음'}`,
            },
          ],
        },
        {
          type: 'section',
          fields: [
            {
              type: 'mrkdwn',
              text: `*가격:*\n${
                rowData && rowData.original_price > 0 ? `${rowData.original_price}원` : '가격 문의'
              }`,
            },
            {
              type: 'mrkdwn',
              text: `*CAS 번호:*\n${
                rowData && rowData.cas_number ? rowData.cas_number : '정보 없음'
              }`,
            },
          ],
        },
        {
          type: 'divider',
        },
        {
          type: 'section',
          text: {
            type: 'mrkdwn',
            text: `*제품 페이지:*\n<https://cacheby.com/@${product.user.username}/products/${product.url_slug}|제품 상세 페이지 바로가기>`,
          },
        },
        {
          type: 'context',
          elements: [
            {
              type: 'mrkdwn',
              text: '⚠️ 빠른 시간 내에 고객 문의에 응답해 주세요.',
            },
          ],
        },
      ],
    };
    axios.post(url, JSON.stringify(data), {
      withCredentials: false,
      // eslint-disable-next-line no-shadow
      transformRequest: [
        // eslint-disable-next-line no-shadow
        (data, headers) => {
          delete headers.post['Content-Type'];
          return data;
        },
      ],
    });
  };

  render() {
    const {
      product,
      username,
      currentUserRule,
      currentUsername,
      askRemove,
      logged,
      gradeDiscount,
    } = this.props;
    const { onChannel, onLogin, refreshProductData } = this;

    if (!product) return <ProductPlaceholder />;
    if (!product.user) return <ProductPlaceholder />;
    const plainTextBody = convertToPlainText(product.body);

    // 쿼리 파라미터에서 part_number 추출
    const urlParams = new URLSearchParams(this.props.location.search);
    const partNumberParam = urlParams.get('part_number');

    const url = `${process.env.BASE_URL}/@${product.user.username}/products/${product.url_slug}`;

    // part_number가 있으면 타이틀에 포함
    let baseTitle;
    let subject;
    let enhancedDescription;
    if (partNumberParam) {
      // 해당 part_number의 상세 정보 찾기
      const selectedVariant = product.product_price.find(p => p.part_number === partNumberParam);
      if (selectedVariant) {
        baseTitle = `${partNumberParam} - ${selectedVariant.description} | 캐시바이`;
        subject = `${partNumberParam} - ${selectedVariant.description}`;
        enhancedDescription = `${partNumberParam} - ${
          selectedVariant.description
        } | ${product.short_description || convertToPlainText(product.body).slice(0, 200)}`;
      } else {
        baseTitle = `${partNumberParam} - ${product.title} | 캐시바이`;
        subject = `${partNumberParam} - ${product.title}`;
        enhancedDescription = `${partNumberParam} - ${product.title} | ${product.short_description || convertToPlainText(
          product.body,
        ).slice(0, 200)}`;
      }
    } else {
      baseTitle = `${product.title} | 캐시바이`;
      subject = product.title;
      enhancedDescription = product.short_description || convertToPlainText(product.body);
    }

    // partNumber로 키워드 만들기
    let partNumber = [];
    let ldJsonArr = [];

    // ProductGroup 구조로 변경 (여러 SKU를 하나의 그룹으로)
    const productVariants = [];

    for (let i = 0; i < product.product_price.length; i += 1) {
      partNumber.push(product.product_price[i].part_number);
      const productPartNumber = product.product_price[i].part_number;
      const productDescription = product.product_price[i].description;
      const productPrice = Math.round(product.product_price[i].price * 1.1);
      const productUnit = product.product_price[i].unit;

      // 각 SKU를 Product variant로 추가
      productVariants.push({
        '@type': 'Product',
        sku: productPartNumber,
        mpn: productPartNumber,
        image: product.thumbnail,
        name: `${productDescription.slice(0, 100).trim()} - ${productPartNumber}`,
        description: productDescription,
        unit: productUnit,
        offers: {
          '@type': 'Offer',
          url: `${url}?part_number=${productPartNumber}`,
          priceCurrency: 'KRW',
          price: productPrice,
          priceValidUntil: '2027-07-01',
          itemCondition: 'https://schema.org/NewCondition',
          availability: 'https://schema.org/InStock',
          seller: {
            '@type': 'Organization',
            name: '캐시바이',
          },
          shippingDetails: {
            '@id': '#shipping_policy',
          },
          hasMerchantReturnPolicy: {
            '@id': '#return_policy',
          },
        },
      });
    }

    // ProductGroup 생성
    const productGroupJson = {
      '@context': 'https://schema.org/',
      '@type': 'ProductGroup',
      name: product.title,
      description: product.short_description || convertToPlainText(product.body).slice(0, 300).trim(),
      url,
      image: product.thumbnail,
      brand: {
        '@type': 'Brand',
        name: product.meta.supplier,
      },
      manufacturer: {
        '@type': 'Organization',
        name: product.meta.supplier,
      },
      audience: {
        '@type': 'PeopleAudience',
        audienceType: 'Researchers',
        suggestedMinAge: 25,
        requiredGender: 'unisex',
        geographicArea: {
          '@type': 'Country',
          name: 'South Korea',
        },
        occupationalCategory: 'Biological Research Scientist',
        educationalUse: 'Laboratory Research',
      },
      productGroupID: product.id,
      variesBy: ['https://schema.org/sku'],
      hasVariant: productVariants,
    };

    ldJsonArr.push(productGroupJson);

    // 반품 정책
    const returnPolicy = {
      '@context': 'https://schema.org/',
      '@type': 'MerchantReturnPolicy',
      '@id': '#return_policy',
      applicableCountry: 'KR',
      returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
      merchantReturnDays: 14,
      returnMethod: 'https://schema.org/ReturnByMail',
      returnFees: 'https://schema.org/FreeReturn',
    };

    ldJsonArr.push(returnPolicy);

    // 배송 정책
    const shippingPolicy = {
      '@context': 'https://schema.org/',
      '@type': 'OfferShippingDetails',
      '@id': '#shipping_policy',
      shippingRate: {
        '@type': 'MonetaryAmount',
        value: 0,
        currency: 'KRW',
      },
      shippingDestination: {
        '@type': 'DefinedRegion',
        addressCountry: 'KR',
      },
      deliveryTime: {
        '@type': 'ShippingDeliveryTime',
        handlingTime: {
          '@type': 'QuantitativeValue',
          minValue: 0,
          maxValue: 1,
          unitCode: 'DAY',
        },
        transitTime: {
          '@type': 'QuantitativeValue',
          minValue: 1,
          maxValue: 5,
          unitCode: 'DAY',
        },
      },
    };

    ldJsonArr.push(shippingPolicy);

    // Breadcrumb 구조화된 데이터 생성 (여러 경로 지원)
    const createBreadcrumbStructuredData = categoryList => {
      if (!categoryList || categoryList.length === 0) return [];

      // 카테고리 맵 생성
      const categoryMap = new Map();
      categoryList.forEach(category => {
        categoryMap.set(category.id, category);
      });

      // 모든 리프 노드(자식이 없는 카테고리) 찾기
      const findLeafCategories = () => {
        const hasChildren = new Set();
        categoryList.forEach(category => {
          if (category.parent) {
            hasChildren.add(category.parent);
          }
        });

        return categoryList.filter(category => !hasChildren.has(category.id));
      };

      // 특정 카테고리부터 루트까지의 경로 생성
      const buildPathFromCategory = startCategory => {
        const path = [];
        let currentCategory = startCategory;

        while (currentCategory) {
          path.unshift(currentCategory);
          currentCategory = currentCategory.parent ? categoryMap.get(currentCategory.parent) : null;
        }

        return path;
      };

      const leafCategories = findLeafCategories();
      const breadcrumbLists = [];

      // 각 리프 카테고리에서 루트까지의 경로를 breadcrumb으로 생성
      leafCategories.forEach(leafCategory => {
        const categoryPath = buildPathFromCategory(leafCategory);

        if (categoryPath.length > 0) {
          const breadcrumbItems = categoryPath.map((category, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            name: category.name,
            item: `${process.env.BASE_URL}/categories/${category.url_slug}`,
          }));

          breadcrumbLists.push({
            '@context': 'https://schema.org/',
            '@type': 'BreadcrumbList',
            itemListElement: breadcrumbItems,
          });
        }
      });

      return breadcrumbLists;
    };

    // 제품에 카테고리 정보가 있다면 breadcrumb 추가
    if (product.categories && product.categories.length > 0) {
      const breadcrumbDataList = createBreadcrumbStructuredData(product.categories);
      if (breadcrumbDataList && breadcrumbDataList.length > 0) {
        ldJsonArr.push(...breadcrumbDataList);
      }
    }

    // 헬멧에 들어 갈수 있도록 가공 처리
    const keywords = partNumber.join();
    const ldJsonData = JSON.stringify(ldJsonArr);

    return (
      <Fragment>
        <Helmet>
          <title>{baseTitle}</title>
          <meta name="title" content={baseTitle} />
          <meta name="subject" content={subject} />
          <meta name="publisher" content="연구원들은 다 아는 쇼핑몰 | 캐시바이" />
          <meta name="author" content="캐시바이" />
          <meta name="robots" content="index,follow" />
          <meta name="keywords" content={keywords} />
          <meta name="description" content={enhancedDescription} />
          <meta property="og:url" content={url} />
          <meta property="og:site_name" content="연구원들은 다 아는 쇼핑몰 | 캐시바이" />
          <meta property="og:author" content="캐시바이" />
          <meta property="og:type" content="product" />
          <meta property="og:title" content={baseTitle} />
          <meta property="og:description" content={enhancedDescription} />
          {product.thumbnail && <meta property="og:image" content={product.thumbnail} />}
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={baseTitle} />
          <meta name="twitter:description" content={enhancedDescription} />
          {product.thumbnail && <meta name="twitter:image" content={product.thumbnail} />}
          {ldJsonArr.length && <script type="application/ld+json">{ldJsonData}</script>}
        </Helmet>
        <ProductCategories categories={product.categories} />
        {product.series && <ProductSeriesInfo series={product.series} username={username || ''} />}
        <ProductContent
          isPrivate={product.is_private}
          title={product.title}
          url={this.props.match.url}
          user={product.user}
          informCopy={this.informCopy}
          onToggleLike={this.onToggleLike}
          likes={product.likes}
          liked={product.liked}
          logged={logged}
          onLogin={onLogin}
          ownProduct={currentUsername === username || currentUserRule === 'admin'}
          id={product.id}
          onAskRemove={this.onToggleAskRemove}
          product={product}
          thumbnail={product.thumbnail}
          body={product.body}
          theme={product.meta.code_theme}
          price={product.meta.price}
          supplier={product.meta.supplier}
          productUrl={product.meta.productUrl}
          productPrice={product.product_price}
          currentUsername={currentUsername}
          onChannel={onChannel}
          username={product.user.username}
          displayName={product.user.display_name}
          tags={product.tags}
          categories={product.categories}
          gradeDiscount={gradeDiscount}
          badges={product.badges}
          refreshProductData={refreshProductData}
          shortDescription={product.short_description}
        />
        <ProductRegulationExcReturn />
        <QuestionModal
          open={askRemove}
          title="상품 삭제"
          description="이 상품을 정말로 삭제하시겠습니까?"
          confirmText="삭제"
          onConfirm={this.onConfirmRemove}
          onCancel={this.onToggleAskRemove}
        />
      </Fragment>
    );
  }
}

export default connect(
  ({ products, pender, user, common }: State) => ({
    currentUsername: user.user && user.user.username,
    currentUserRule: user.user && user.user.rule,
    currentUser: user.user,
    product: products.product,
    likeInProcess: pender.pending['products/LIKE'] || pender.pending['products/UNLIKE'],
    askRemove: products.askRemove,
    routerHistory: common.router.history,
    shouldCancel: common.ssr && !common.router.altered,
    logged: !!user.user,
    gradeDiscount: (user.user && user.user.grade.value) || 0,
  }),
  () => ({}),
)(withRouter(ProductViewer));



// WEBPACK FOOTER //
// ./src/containers/product/ProductViewer.js